import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getIdToken } from 'firebase/auth';
import { useAuth } from '../../contexts/AuthContext';
import { useUserPreferences } from '../../contexts/UserPreferencesContext';
import { auth } from '../../firebase';
import './ResourcesPanel.css';

const JOB_TITLE_LABELS = {
  travel_nurse: 'Travel Nurse',
  travel_ir_tech: 'Travel Interventional Radiologic Technologist',
};

const FEEDBACK_PLACEHOLDER =
  "Enter the correct link if you know it and I'll update this for you. Thank you for your support.";

let licenseDataCache = null;
let licenseDataPromise = null;
function getLicenseData() {
  if (licenseDataCache) return Promise.resolve(licenseDataCache);
  if (!licenseDataPromise) {
    licenseDataPromise = fetch('/data/license-data.json')
      .then((r) => r.json())
      .then((data) => { licenseDataCache = data; return data; });
  }
  return licenseDataPromise;
}

function RequirementBadge({ required }) {
  const isRequired = required !== false;
  return (
    <span
      className={`resources-panel__status-badge ${isRequired ? 'resources-panel__status-badge--required' : 'resources-panel__status-badge--optional'}`}
      aria-label={isRequired ? 'State license required' : 'State license not required'}
    >
      <span className="resources-panel__status-dot" aria-hidden="true" />
      {isRequired ? 'Required' : 'Not required'}
    </span>
  );
}

export default function ResourcesPanel({ searchedState, workingAddress = '' }) {
  const { user, loading: authLoading } = useAuth();
  const { jobTitle, favorites } = useUserPreferences();
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);
  const [feedbackThanks, setFeedbackThanks] = useState(false);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!user || !jobTitle || !searchedState) {
      setLicenseInfo(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setLicenseInfo(null);

    getLicenseData()
      .then((data) => {
        if (cancelled) return;
        const stateData = data[jobTitle]?.[searchedState];
        if (!stateData) {
          setLicenseInfo({ supported: false });
        } else {
          setLicenseInfo({ supported: true, ...stateData });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[ResourcesPanel] Failed to load license data:', err);
          setError('Failed to load license info.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [user, jobTitle, searchedState]);

  function openFeedback() {
    setFeedbackText('');
    setFeedbackError(null);
    setFeedbackThanks(false);
    setFeedbackSubmitting(false);
    try {
      dialogRef.current?.showModal();
    } catch (_) {}
  }

  function handleDialogClose() {
    setFeedbackText('');
    setFeedbackError(null);
    setFeedbackThanks(false);
    setFeedbackSubmitting(false);
  }

  async function handleFeedbackSubmit(e) {
    e.preventDefault();
    if (!user || !licenseInfo?.applyUrl || !auth.currentUser) return;
    setFeedbackSubmitting(true);
    setFeedbackError(null);
    try {
      const idToken = await getIdToken(auth.currentUser);
      const res = await fetch('/api/license-url-feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          clickedUrl: licenseInfo.applyUrl,
          userMessage: feedbackText.trim(),
          workAddress: workingAddress || '',
          jobTitleKey: jobTitle || '',
          jobTitleLabel: JOB_TITLE_LABELS[jobTitle] || jobTitle || '',
          stateCode: searchedState || '',
          favorites: (favorites || []).map((f) => ({
            label: f.label,
            mode: f.mode,
            placeId: f.placeId || '',
          })),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `Request failed (${res.status})`);
      }
      setFeedbackThanks(true);
    } catch (err) {
      setFeedbackError(err.message || 'Something went wrong.');
    } finally {
      setFeedbackSubmitting(false);
    }
  }

  function renderBody() {
    if (authLoading) return null;

    if (!user) {
      return (
        <p className="resources-panel__prompt">
          <Link to="/login">Sign in</Link> or <Link to="/signup">create an account</Link> to get customized licensing resources.
        </p>
      );
    }

    if (!jobTitle) {
      return (
        <p className="resources-panel__prompt">
          Set your <Link to="/profile">job title in your profile</Link> to see state licensing info for your role.
        </p>
      );
    }

    if (!searchedState) {
      return (
        <p className="resources-panel__prompt">
          Search a location to see licensing requirements.
        </p>
      );
    }

    if (loading) {
      return <p className="resources-panel__loading">Loading…</p>;
    }

    if (error) {
      return <p className="resources-panel__error">{error}</p>;
    }

    if (licenseInfo?.supported === false) {
      return (
        <p className="resources-panel__prompt">
          Licensing info not yet available for this role.
        </p>
      );
    }

    if (licenseInfo) {
      return (
        <div className="resources-panel__item">
          <p className="resources-panel__role">{JOB_TITLE_LABELS[jobTitle]}</p>

          <div className="resources-panel__section">
            <div className="resources-panel__section-head">
              <p className="resources-panel__section-label">State License</p>
              {licenseInfo.applyUrl && (
                <button
                  type="button"
                  className="resources-panel__wrong-url-btn"
                  onClick={openFeedback}
                >
                  wrong url?
                </button>
              )}
            </div>

            <div className="resources-panel__license-row">
              <span className="resources-panel__state-code">{searchedState}</span>
              <RequirementBadge required={licenseInfo.required} />
            </div>

            {licenseInfo.required === false ? null : licenseInfo.applyUrl ? (
              <a
                href={licenseInfo.applyUrl}
                className="resources-panel__apply-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Open license application →
              </a>
            ) : (
              <p className="resources-panel__prompt">
                Check with your state licensing board directly.
              </p>
            )}
          </div>

          {jobTitle === 'travel_nurse' && typeof licenseInfo.compactLicenseAccepted === 'boolean' && (
            <div className="resources-panel__section">
              <p className="resources-panel__section-label">Compact License</p>
              <p className={licenseInfo.compactLicenseAccepted ? 'resources-panel__compact-yes' : 'resources-panel__compact-no'}>
                {licenseInfo.compactLicenseAccepted ? 'Accepted' : 'Not Accepted'}
              </p>
            </div>
          )}

          <dialog
            ref={dialogRef}
            className="resources-panel__dialog"
            aria-labelledby={feedbackThanks ? 'resources-feedback-thanks' : 'resources-feedback-title'}
            onClose={handleDialogClose}
          >
            <form className="resources-panel__dialog-inner" onSubmit={handleFeedbackSubmit}>
              {!feedbackThanks && (
                <h2 id="resources-feedback-title" className="resources-panel__dialog-title">
                  Oops! My mistake!
                </h2>
              )}
              {feedbackThanks ? (
                <>
                  <p id="resources-feedback-thanks" className="resources-panel__dialog-thanks">
                    Thank you! Your feedback has been sent.
                  </p>
                  <div className="resources-panel__dialog-actions">
                    <button
                      type="button"
                      className="resources-panel__dialog-btn resources-panel__dialog-btn--primary"
                      onClick={() => dialogRef.current?.close()}
                    >
                      Close
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <label htmlFor="resources-feedback-text" className="resources-panel__dialog-label">
                    Help us fix the apply link
                  </label>
                  <textarea
                    id="resources-feedback-text"
                    className="resources-panel__dialog-textarea"
                    value={feedbackText}
                    onChange={(ev) => setFeedbackText(ev.target.value)}
                    placeholder={FEEDBACK_PLACEHOLDER}
                    rows={4}
                    disabled={feedbackSubmitting}
                  />
                  {feedbackError && (
                    <p className="resources-panel__dialog-error" role="alert">{feedbackError}</p>
                  )}
                  <div className="resources-panel__dialog-actions">
                    <button
                      type="button"
                      className="resources-panel__dialog-btn"
                      onClick={() => dialogRef.current?.close()}
                      disabled={feedbackSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="resources-panel__dialog-btn resources-panel__dialog-btn--primary"
                      disabled={feedbackSubmitting}
                    >
                      {feedbackSubmitting ? 'Sending…' : 'Submit'}
                    </button>
                  </div>
                </>
              )}
            </form>
          </dialog>
        </div>
      );
    }

    return null;
  }

  return (
    <aside className="resources-panel" aria-label="License information">
      <h2 className="resources-panel__heading">License Info</h2>
      {renderBody()}
    </aside>
  );
}
